import React, { useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Modal,
  PageHeader,
  Progress,
  Tag,
} from 'antd';
import moment from 'moment';
import Content from '../../component/Content';
import CronRunHistory from '../../component/EngagementCaseReport/CronRunHistory';
import TriggerModal from './TriggerModal';
import ClearAggregationCacheModal from './ClearAggregationCacheModal';
import '../../component/EngagementCaseReport/engagement-case-report.scss';
import {
  triggerCronAction,
  setCurrentModalAction,
  fetchCronStatusAction,
  stopPollingAction,
  resetCronAction,
  clearAggregationCacheAction,
} from './actions';

const TRIGGER_MODAL = 'TRIGGER_MODAL';
const QUEUE_MODAL = 'QUEUE_MODAL';
const CLEAR_CACHE_MODAL = 'CLEAR_CACHE_MODAL';

const RunDetails = ({ cronStatus }) => {
  const {
    months = [],
    currentMonth,
    completedMonths = [],
    force,
    hubIds,
    startedAt,
    lastRan,
    lastError,
    isCronRunning,
    resetBy,
    resetAt,
  } = cronStatus || {};

  if (!months.length && !lastRan && !startedAt) return null;

  const wasManuallyReset = Boolean(resetAt);
  const timeLabel = isCronRunning ? 'Started at' : 'Finished at';
  const timeValue = isCronRunning ? startedAt : lastRan;
  const isRange = months.length > 1;
  const progressPercent =
    isRange && months.length
      ? Math.round((completedMonths.length / months.length) * 100)
      : null;
  const monthsLabel = isRange
    ? `${months[0]} → ${months[months.length - 1]} (${months.length} months)`
    : months[0] || '—';

  return (
    <Descriptions size="small" column={2} style={{ marginTop: 12 }}>
      <Descriptions.Item
        label={isRange ? 'Month range' : 'Month'}
        span={isRange ? 2 : 1}
      >
        {monthsLabel}
      </Descriptions.Item>

      {isRange && isCronRunning && (
        <Descriptions.Item label="Progress" span={2}>
          <Progress
            percent={progressPercent}
            status={lastError ? 'exception' : 'active'}
            format={() =>
              `${completedMonths.length}/${months.length}${
                currentMonth ? ` — processing ${currentMonth}` : ''
              }`
            }
          />
        </Descriptions.Item>
      )}

      {isRange && completedMonths.length > 0 && (
        <Descriptions.Item label="Completed months" span={2}>
          {completedMonths.join(', ')}
        </Descriptions.Item>
      )}

      <Descriptions.Item label="Force reprocess">
        {force ? 'Yes' : 'No'}
      </Descriptions.Item>
      <Descriptions.Item label="Hub IDs">
        {hubIds && hubIds.length > 0 ? hubIds.join(', ') : 'All hubs'}
      </Descriptions.Item>
      <Descriptions.Item label={timeLabel}>
        {timeValue ? moment(timeValue).format('YYYY-MM-DD HH:mm:ss') : '—'}
      </Descriptions.Item>
      {lastError && (
        <Descriptions.Item label="Last error" span={2}>
          <span style={{ color: '#ff4d4f' }}>{lastError}</span>
        </Descriptions.Item>
      )}
      {wasManuallyReset && (
        <Descriptions.Item label="Manually reset" span={2}>
          <span style={{ color: '#faad14' }}>
            {moment(resetAt).format('YYYY-MM-DD HH:mm:ss')}
            {resetBy ? ` by user #${resetBy}` : ''}
          </span>
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};

const EngagementCaseReportContainer = ({
  triggerCron,
  triggerLoading,
  resetCron,
  resetLoading,
  clearAggregationCache,
  clearCacheLoading,
  currentModal,
  setCurrentModal,
  fetchCronStatus,
  stopPolling,
  cronStatus,
}) => {
  useEffect(() => {
    fetchCronStatus();
    return () => stopPolling();
  }, [fetchCronStatus, stopPolling]);

  const { isCronRunning, lastError, startedAt } = cronStatus || {};

  const handleForceReset = () => {
    Modal.confirm({
      title: 'Force Reset Stuck Cron?',
      content: (
        <span>
          This will immediately clear the <strong>isCronRunning</strong> flag.
          Use this only if the worker has crashed and the cron is stuck.
          {startedAt && (
            <>
              <br />
              <br />
              Started at:{' '}
              <strong>{moment(startedAt).format('YYYY-MM-DD HH:mm:ss')}</strong>
            </>
          )}
        </span>
      ),
      okText: 'Yes, Force Reset',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => resetCron(),
    });
  };

  let alertType = 'info';
  if (lastError) alertType = 'error';
  else if (isCronRunning) alertType = 'warning';

  const alertMessage = () => {
    if (isCronRunning) return 'Cron is currently running…';
    if (lastError) return 'Last run failed';
    if (cronStatus && cronStatus.lastRan)
      return 'Last run completed successfully';
    return 'No recent runs recorded.';
  };

  return (
    <>
      <PageHeader
        title="Engagement Case Report Cron"
        className="engagement-case-report"
        subTitle="Trigger the monthly engagement case report cron on demand"
        extra={[
          isCronRunning && (
            <Tag key="running-tag" color="red">
              Running…
            </Tag>
          ),
          isCronRunning && (
            <Button
              key="reset"
              type="danger"
              icon="warning"
              onClick={handleForceReset}
              loading={resetLoading}
              disabled={resetLoading}
            >
              Force Reset
            </Button>
          ),
          <Button
            key="queue"
            icon="plus"
            onClick={() => setCurrentModal(QUEUE_MODAL)}
            disabled={triggerLoading}
            loading={triggerLoading}
          >
            Add job to queue
          </Button>,
          <Button
            key="clear-cache"
            icon="delete"
            onClick={() => setCurrentModal(CLEAR_CACHE_MODAL)}
            disabled={clearCacheLoading}
            loading={clearCacheLoading}
          >
            Clear aggregation cache
          </Button>,
          <Button
            key="trigger"
            type="primary"
            icon="thunderbolt"
            onClick={() => setCurrentModal(TRIGGER_MODAL)}
            disabled={isCronRunning || triggerLoading}
            loading={triggerLoading}
          >
            Trigger
          </Button>,
        ]}
      >
        <Divider className="engagement-case-report__section-divider" />
        <Alert type={alertType} message={alertMessage()} showIcon />
        <RunDetails cronStatus={cronStatus} />
      </PageHeader>
      <Content>
        <CronRunHistory />
      </Content>
      {currentModal === TRIGGER_MODAL && (
        <TriggerModal
          onSave={triggerCron}
          onCancel={() => setCurrentModal(null)}
          triggerLoading={triggerLoading}
        />
      )}
      {currentModal === QUEUE_MODAL && (
        <TriggerModal
          queueMode
          onSave={triggerCron}
          onCancel={() => setCurrentModal(null)}
          triggerLoading={triggerLoading}
        />
      )}
      {currentModal === CLEAR_CACHE_MODAL && (
        <ClearAggregationCacheModal
          onSave={clearAggregationCache}
          onCancel={() => setCurrentModal(null)}
          clearCacheLoading={clearCacheLoading}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  triggerCron: triggerCronAction,
  setCurrentModal: setCurrentModalAction,
  fetchCronStatus: fetchCronStatusAction,
  stopPolling: stopPollingAction,
  resetCron: resetCronAction,
  clearAggregationCache: clearAggregationCacheAction,
};

const mapStateToProps = ({
  engagementCaseReport: {
    triggerLoading,
    resetLoading,
    clearCacheLoading,
    currentModal,
    cronStatus,
  },
}) => ({
  triggerLoading,
  resetLoading,
  clearCacheLoading,
  currentModal,
  cronStatus,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EngagementCaseReportContainer)
);
